import React, { useState } from 'react';
import {
  Box,
  Paper,
  IconButton,
  Typography,
  TextField,
  Fab,
  Collapse,
  Avatar
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import ChatIcon from '@mui/icons-material/Chat';
import SmartToyIcon from '@mui/icons-material/SmartToy'; // Using MUI icon instead of image

const Chatbot = ({ isHomePage }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { text: 'Hello! How can I help you today?', isBot: true }
  ]);
  const [input, setInput] = useState('');

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    // Add user message
    const userMessage = { text: input, isBot: false };
    setMessages(prev => [...prev, userMessage]);
    setInput('');

    // Simulate bot response - Replace with actual chatbot logic
    setTimeout(() => {
      const botMessage = { 
        text: "I'm here to help! How can I assist you today?", 
        isBot: true 
      };
      setMessages(prev => [...prev, botMessage]);
    }, 1000);
  };

  const chatWindow = (
    <Paper
      elevation={3}
      sx={{
        width: isHomePage ? '100%' : 320,
        height: isHomePage ? '100%' : 450,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        ...(isHomePage ? {
          borderRadius: 0,
        } : {
          position: 'fixed',
          bottom: 90,
          right: 20,
          zIndex: 1000,
        })
      }}
    >
      {/* Chat header */}
      <Box
        sx={{
          p: 2,
          bgcolor: 'primary.main',
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SmartToyIcon />
          <Typography variant="h6">Chat Assistant</Typography>
        </Box>
        {!isHomePage && (
          <IconButton 
            size="small" 
            onClick={() => setIsOpen(false)}
            sx={{ color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      {/* Chat messages */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 1
        }}
      >
        {messages.map((message, index) => (
          <Box
            key={index}
            sx={{
              display: 'flex',
              justifyContent: message.isBot ? 'flex-start' : 'flex-end',
              gap: 1
            }}
          >
            {message.isBot && (
              <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                <SmartToyIcon fontSize="small" />
              </Avatar>
            )}
            <Paper
              sx={{
                p: 1,
                maxWidth: '70%',
                bgcolor: message.isBot ? 'grey.100' : 'primary.main',
                color: message.isBot ? 'text.primary' : 'white'
              }}
            >
              <Typography variant="body2">{message.text}</Typography>
            </Paper>
          </Box>
        ))}
      </Box>

      {/* Chat input */}
      <Box
        component="form"
        onSubmit={handleSend}
        sx={{
          p: 2,
          borderTop: 1,
          borderColor: 'divider',
          display: 'flex',
          gap: 1
        }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder="Type a message..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          variant="outlined"
        />
        <IconButton 
          type="submit" 
          color="primary"
          disabled={!input.trim()}
        >
          <SendIcon />
        </IconButton>
      </Box>
    </Paper>
  );

  // For homepage, return just the chat window
  if (isHomePage) {
    return chatWindow;
  }

  // For other pages, return the floating button and collapsible chat window
  return (
    <>
      <Fab
        color="primary"
        aria-label="chat"
        onClick={() => setIsOpen(!isOpen)}
        sx={{
          position: 'fixed',
          bottom: 20,
          right: 20,
          zIndex: 1000
        }}
      >
        <ChatIcon />
      </Fab>

      <Collapse in={isOpen}>
        {chatWindow}
      </Collapse>
    </>
  );
};

export default Chatbot; 